// ============================================================================
// Manager Dashboard
// Feature: 136-dashboard-alignment (T026-T030)
// Created: 2026-01-11
// Updated: 2026-02-17 - Replaced mock data with live API calls (Constitution XXII)
// Description: Summary dashboards with business metrics for managers
// ============================================================================

// Updated: 2026-05-31 — Spec 160 T022: client-scoped KPI panels (no CollectionHealth)
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { BarChart3 } from 'lucide-react';
import { CuFleetPanel } from '../../components/Dashboard/panels/CuFleetPanel';
import { StoragePanel } from '../../components/Dashboard/panels/StoragePanel';
import { GovernancePanel } from '../../components/Dashboard/panels/GovernancePanel';
import { EntityGrowthPanel } from '../../components/Dashboard/panels/EntityGrowthPanel';
import { apiClient } from '../../services/apiClient';
import type { DashboardKpis } from '../../../../shared/types/dashboard';
import { DualScrollTable, usePagination, PaginationControls } from '../../components/common/TableControls';
import { Extended } from '../../components/layout/Responsive';

import SummaryMetricsWidget from '../../components/Dashboard/SummaryMetricsWidget';
import { ComingSoonWidget } from '../../components/Dashboard/widgets/ComingSoonWidget';
import { WidgetSkeleton } from '../../components/Dashboard/widgets/WidgetSkeleton';
import { WidgetErrorState } from '../../components/Dashboard/widgets/WidgetErrorState';
import { healthApi } from '../../services/healthApi';
import { metricsApi } from '../../services/metricsApi';
import { PageHelpPanel } from '../../components/ui/PageHelpPanel';
import { PageLayout, DashboardPanel, StatCard, ErrorState } from '../../components/ui';
import { useScreenRefresh } from '../../hooks/useScreenRefresh';
import { lastCollectedAt } from '../../utils/lastCollectedAt';
import { managerDashboardHelp } from '../../data/help/managerDashboardHelp';
import { clientApi, type ClientSummaryRow } from '../../services/clientApi';
import { AccessGate } from '../../components/access/AccessGate';
import { useHasAccess } from '../../hooks/useAccess';
import { unmeasuredSizeNote } from '@/utils/unmeasuredSize';
import { slaComplianceFrom } from '@/utils/slaCompliance';
import { backupCoverageFrom } from '@/utils/backupCoverage';

interface BusinessMetrics {
  /** Null when the KPI payload's entities section could not be read (Spec 298 FR-005). */
  totalWorkspaces: number | null;
  activeWorkspaces: number | null;
  /** Null when the cuFleet section could not be read (Spec 298 FR-005). */
  totalCapacities: number | null;
  /** Null when the storage section could not be read (Spec 298 FR-005). */
  dataVolume: string | null;
  /** How many lakehouses/warehouses dataVolume counts at their last known size (T159 item 32). */
  dataVolumeNote?: string;
  /** Null when there is nothing to protect, or the backup tiles are not licensed (Spec 298 FR-004). */
  backupCoverage: number | null;
  backupNothingToProtect: boolean;
  /** Month-to-date SLA compliance, or null when the read failed (Spec 292 D5: never a 0 for a failed read). */
  slaCompliance: number | null;
  slaUnavailable?: boolean;
}

/** Spec 298 FR-004: the tile and the Resource Summary row say "nothing to protect" over no protectable entity. */
const backupCoverageText = (m: BusinessMetrics): string =>
  m.backupNothingToProtect ? 'nothing to protect' : m.backupCoverage === null ? '—' : `${m.backupCoverage}%`;

const ManagerDashboard: React.FC = () => {
  // KPI panels (Portfolio Overview) — real data
  const kpisQuery = useQuery<DashboardKpis>({
    queryKey: ['dashboard', 'kpis', 'Manager'],
    queryFn: async () => (await apiClient.get<DashboardKpis>('/api/dashboards/kpis')).data,
    staleTime: 55_000, refetchInterval: 60_000, refetchOnWindowFocus: false,
  });
  const kpis = kpisQuery.data;
  // Spec 226: the panels poll for themselves (owner decision 10); the header Refresh re-fetches them all.
  const pageRefresh = useScreenRefresh();
  // A failed load shows an error with a retry in place of the panels (below), never zeros and never a skeleton that
  // waits for good.
  const kpisLoading = kpisQuery.isLoading;

  // Spec 285: backup coverage is a backup tile — asked for, and shown, only for a backup-licensed
  // tenant (SuperAdmin exempt). An unlicensed tenant's API would answer 403.
  const canSeeBackup = useHasAccess('dashboard.backup-tiles');

  // SLA + backup coverage — remove broken capacity-summaries call; fix SLA array shape mismatch
  const metricsQuery = useQuery<Pick<BusinessMetrics, 'backupCoverage' | 'backupNothingToProtect' | 'slaCompliance' | 'slaUnavailable'>>({
    queryKey: ['manager', 'dashboard', 'metrics', canSeeBackup],
    queryFn: async () => {
      const [slaRes, coverageRes] = await Promise.all([
        healthApi.getSlaStatus(),
        canSeeBackup ? metricsApi.getBackupCoverage() : Promise.resolve(null),
      ]);
      // Spec 292 D5: a failed SLA read (the route's 503, success: false) is "unavailable", never 0; backup coverage
      // from the same Promise.all is unaffected (healthApi resolves a failed call to { success: false }).
      // Spec 298 FR-004: nothing to protect is said so, never 0% (backupCoverage.ts)
      return { ...backupCoverageFrom(coverageRes), ...slaComplianceFrom(slaRes) };
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30_000,
  });

  // T027: Live API call for client breakdown
  const clientsQuery = useQuery<ClientSummaryRow[]>({
    queryKey: ['manager', 'dashboard', 'clients'],
    queryFn: async () => {
      const summary = await clientApi.getSummary();
      return summary.clients || [];
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30_000,
  });

  // Derive BusinessMetrics from KPI panels + SLA/backup queries — replaces broken capacity-summaries
  const metrics = useMemo<BusinessMetrics | undefined>(() => {
    if (!kpis || !metricsQuery.data) return undefined;
    // 1024³, not 1e9 — see AdminDashboard: this adds bytes-derived GB to collector-stored GiB,
    // so a decimal divisor makes the terms incommensurable and inflates the total.
    // Spec 298 FR-005: a section whose read failed is null, and its tile says so instead of printing 0.
    const storage = kpis.storage;
    const totalStorageGb = storage
      ? (storage.totalAzureBytes / 1024 ** 3) + storage.totalLakehouseGb + storage.totalWarehouseGb
      : null;
    // Binary ladder — see AdminDashboard. GiB -> TB is 1024, GiB -> PB is 1024².
    const dataVolume = totalStorageGb == null ? null
      : totalStorageGb >= 1024 ** 2
      ? `${(totalStorageGb / 1024 ** 2).toFixed(2)} PB`
      : totalStorageGb >= 1024
      ? `${(totalStorageGb / 1024).toFixed(2)} TB`
      : `${totalStorageGb.toFixed(1)} GB`;
    return {
      totalWorkspaces:  kpis.entities ? kpis.entities.workspaces.current : null,
      activeWorkspaces: kpis.entities ? kpis.entities.workspaces.current : null,
      totalCapacities:  kpis.cuFleet ? kpis.cuFleet.total : null,
      dataVolume,
      // Spec 286 T159 item 32: the lakehouse and warehouse terms count items the last collection did not size at
      // their last known size (the merge keeps it, Spec 238); the tile says how many.
      dataVolumeNote: storage ? unmeasuredSizeNote((storage.lakehouseUnmeasured ?? 0) + (storage.warehouseUnmeasured ?? 0)) : undefined,
      backupCoverage:   metricsQuery.data.backupCoverage,
      backupNothingToProtect: metricsQuery.data.backupNothingToProtect ?? false,
      slaCompliance:    metricsQuery.data.slaCompliance,
      slaUnavailable:   metricsQuery.data.slaUnavailable ?? false,
    };
  }, [kpis, metricsQuery.data]);

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(clientsQuery.data ?? []);

  return (
    <PageLayout
      title="Manager Dashboard"
      description="Business metrics and resource utilization overview"
      icon={<BarChart3 className="h-6 w-6" />}
      actions={<PageHelpPanel content={managerDashboardHelp} buttonSize="sm" />}
      onRefresh={pageRefresh.refresh}
      isRefreshing={pageRefresh.isRefreshing}
      lastFetchedAt={kpisQuery.dataUpdatedAt}
      collectedAt={lastCollectedAt(kpis?.collectionHealth)}
      refreshError={pageRefresh.error ?? kpisQuery.error}
    >
      {/* Spec 160 T022: client-scoped KPI panels (no CollectionHealth) */}
      {kpisQuery.isError && !kpis ? (
        <ErrorState
          className="mb-[var(--space-section)]"
          title="Dashboard figures could not be loaded"
          message="The capacity, storage, governance and growth figures did not arrive."
          onRetry={() => void kpisQuery.refetch()}
        />
      ) : (
      <div className="mb-[var(--space-section)]">
        {/* Row 1: CU Fleet + Storage (no Collection Health for Manager) */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CuFleetPanel data={kpis?.cuFleet} loading={kpisLoading} />
          <StoragePanel data={kpis?.storage} loading={kpisLoading} showCostEstimate />
        </div>
        {/* Row 2: Governance full width left, entity counts right */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <GovernancePanel data={kpis?.governance} loading={kpisLoading} />
          <div className="grid grid-cols-2 gap-4">
            <EntityGrowthPanel label="Workspaces" data={kpis?.entities?.workspaces} loading={kpisLoading} />
            <EntityGrowthPanel label="Lakehouses" data={kpis?.entities?.lakehouses} loading={kpisLoading} color="#0ea5e9" />
            <EntityGrowthPanel label="Warehouses" data={kpis?.entities?.warehouses} loading={kpisLoading} color="#f59e0b" />
            <EntityGrowthPanel label="Container Apps" data={kpis?.entities?.containerApps} loading={kpisLoading} color="#10b981" />
          </div>
        </div>
      </div>
      )}

      {/* Key Metrics — T030: zero-state and error handling */}
      {(metricsQuery.isLoading || kpisLoading) ? (
        <div className="mb-[var(--space-section)] grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-[var(--shadow-md)]">
              <div className="mb-2 h-4 w-2/3 rounded bg-[var(--color-bg-subtle)]" />
              <div className="h-8 w-1/2 rounded bg-[var(--color-bg-subtle)]" />
            </div>
          ))}
        </div>
      ) : metricsQuery.isError ? (
        <WidgetErrorState
          title="Business Metrics"
          message="Failed to load business metrics"
          onRetry={() => metricsQuery.refetch()}
          className="mb-[var(--space-section)]"
        />
      ) : metrics ? (
        <div className="mb-[var(--space-section)] grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard
            label="Total Workspaces"
            value={metrics.totalWorkspaces ?? '—'}
            subtext={metrics.totalWorkspaces == null ? 'workspaces could not be read' : 'across all capacities'}
            tooltip="Total Fabric workspaces under management across every capacity."
          />
          <StatCard
            label="Data Volume"
            value={metrics.dataVolume ?? '—'}
            subtext={metrics.dataVolume == null ? 'storage figures could not be read'
              : metrics.dataVolumeNote ? `Azure + OneLake + warehouse · ${metrics.dataVolumeNote}` : 'Azure + OneLake + warehouse'}
            tooltip="Total managed storage: Azure storage accounts + OneLake (lakehouse) + warehouse."
          />
          <AccessGate area="dashboard.backup-tiles">
          <StatCard
            label="Backup Coverage"
            value={backupCoverageText(metrics)}
            valueClassName={
              metrics.backupCoverage === null ? undefined
              : metrics.backupCoverage >= 95 ? 'text-[var(--color-status-success)]' : metrics.backupCoverage >= 80 ? 'text-[var(--color-status-warning)]' : 'text-[var(--color-status-danger)]'
            }
            subtext="Protected ÷ protectable"
            tooltip="Protected ÷ protectable across lakehouses, warehouses and workspace-DR — an entity is protected when it's the target of an active backup plan."
          />
          </AccessGate>
          <StatCard
            label="SLA Compliance"
            value={metrics.slaCompliance == null ? '—' : `${metrics.slaCompliance}%`}
            valueClassName={
              metrics.slaCompliance == null ? undefined
                : metrics.slaCompliance >= 99 ? 'text-[var(--color-status-success)]' : metrics.slaCompliance >= 95 ? 'text-[var(--color-status-warning)]' : 'text-[var(--color-status-danger)]'
            }
            subtext={metrics.slaUnavailable ? 'SLA status could not be read' : 'This month'}
            tooltip="Month-to-date availability measured against each tier's contracted SLA target."
          />
        </div>
      ) : null}

      {/* Main Content Grid.
          Spec 197 US4: extended detail (client table, compliance/cost placeholders,
          resource summary) hidden on mobile — quick glance = Portfolio Overview + Key Metrics. */}
      <Extended>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* T027: Client Breakdown (replaces mock Department Summary) */}
        <div className="lg:col-span-3">
          <DashboardPanel
            title="Client Breakdown"
            actions={
              <Link to="/admin/clients" className="text-sm text-[var(--color-accent)] hover:underline">
                View Details →
              </Link>
            }
          >
            {clientsQuery.isLoading ? (
              <WidgetSkeleton rows={5} title="Loading clients..." />
            ) : clientsQuery.isError ? (
              <WidgetErrorState
                message="Failed to load client data"
                onRetry={() => clientsQuery.refetch()}
              />
            ) : clientsQuery.data && clientsQuery.data.length === 0 ? (
              <div className="py-8 text-center text-[var(--color-text-secondary)]">
                <svg className="mx-auto mb-3 h-12 w-12 text-[var(--color-text-secondary)] opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="font-medium text-[var(--color-text-primary)]">No clients configured</p>
                <p className="mt-1 text-sm">Add clients in Settings → Clients to see breakdown data here.</p>
                <Link to="/admin/clients" className="mt-3 inline-block text-sm text-[var(--color-accent)] hover:underline">
                  Go to Client Management →
                </Link>
              </div>
            ) : (
              <>
                <DualScrollTable>
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                      <th className="px-2 py-1.5">Client</th>
                      <th className="px-2 py-1.5 text-right">SLA Tier</th>
                      <th className="px-2 py-1.5 text-right">Capacities</th>
                      <th className="px-2 py-1.5 text-right">Workspaces</th>
                      <th className="px-2 py-1.5 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((client, index) => (
                      <tr key={client.id} className={clsx('border-b border-[var(--color-border)] last:border-0', index === 0 && 'bg-blue-50 dark:bg-blue-900/20')}>
                        <td className="px-2 py-1.5 text-sm font-medium text-[var(--color-text-primary)]">{client.client_name}</td>
                        <td className="px-2 py-1.5 text-right text-sm text-[var(--color-text-secondary)]">{client.sla_tier || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-sm text-[var(--color-text-secondary)]">{client.capacity_count}</td>
                        <td className="px-2 py-1.5 text-right text-sm text-[var(--color-text-secondary)]">{client.workspace_count}</td>
                        <td className="px-2 py-1.5 text-right text-sm">
                          <span className={clsx(
                            'rounded px-2 py-0.5 text-xs font-medium',
                            client.is_active
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                          )}>
                            {client.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </DualScrollTable>
                <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
              </>
            )}
          </DashboardPanel>
        </div>

        {/* T028: Cost Estimation — replaced with ComingSoonWidget */}
        <div className="lg:col-span-3">
          <ComingSoonWidget
            title="Cost Estimation"
            description="Workspace cost allocation based on capacity usage. Monthly spend tracking with budget alerts and trend analysis across all 4 capacity types."
            icon={
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>

        {/* Summary Metrics Widget — computed from live query data */}
        <div className="lg:col-span-3">
          <SummaryMetricsWidget
            title="Resource Summary"
            metrics={metrics ? [
              // Spec 298 FR-005/FR-006: a value that was not read shows "—", never 0 or "null%"
              { label: 'Capacities', value: metrics.totalCapacities ?? '—', description: 'Total configured capacities' },
              { label: 'Workspaces', value: metrics.totalWorkspaces ?? '—', description: 'Total workspaces across capacities' },
              { label: 'Active Workspaces', value: metrics.activeWorkspaces ?? '—', description: 'Workspaces in healthy state' },
              ...(canSeeBackup
                ? [{ label: 'Backup Coverage', value: backupCoverageText(metrics), description: 'Protected ÷ protectable entities (active plans)' }]
                : []),
              { label: 'SLA Compliance', value: metrics.slaCompliance == null ? '—' : `${metrics.slaCompliance}%`, description: 'Current month SLA compliance' },
              { label: 'Clients', value: clientsQuery.data ? clientsQuery.data.length : '—', description: 'Registered client accounts' },
            ] : []}
          />
        </div>
      </div>
      </Extended>
    </PageLayout>
  );
};

export default ManagerDashboard;
